<template>
  <div class="energy-con-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$mgh₁ + ½mv₁² = mgh₂ + ½mv₂²$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>实时总动能 (Ek)</label>
              <span class="val">{{ ek.toFixed(1) }} <small>J</small></span>
           </div>
           <div class="hud-item orange">
              <label>实时重力势能 (Ep)</label>
              <span class="val" :style="{color: isDumping ? '#f43f5e' : '#f97316'}">{{ ep.toFixed(1) }} <small>J</small></span>
           </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240" preserveAspectRatio="xMidYMid meet">
          <defs>
             <marker id="arr-g" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#94a3b8" /></marker>
          </defs>

          <!-- 低耗摩擦轨道 (Track) -->
          <path :d="trackPath" 
                fill="none" stroke="#1e293b" stroke-width="6" stroke-linecap="round" />
          <path :d="trackPath" 
                fill="none" stroke="#334155" stroke-width="1.5" stroke-dasharray="4,4" />

          <!-- 模拟参考点 -->
          <line x1="20" y1="210" x2="380" y2="210" stroke="#1e293b" stroke-width="1" stroke-dasharray="2,2" />
          <text x="25" y="205" fill="#475569" font-size="8">零势能参考面 (h=0)</text>

          <!-- 运动小球 (Ball) -->
          <circle :cx="ballPos.x" :cy="ballPos.y" r="10" fill="#0ea5e9" />
          <circle :cx="ballPos.x" :cy="ballPos.y" r="6" fill="#f8fafc" opacity="0.3" />

          <!-- 能量转化示意 (动态反馈) -->
          <g transform="translate(340, 20)">
             <rect width="40" height="100" fill="rgba(15,23,42,0.8)" rx="4" />
             <!-- 这里的能量柱必须实时对齐逻辑 -->
             <rect x="10" :y="90 - Math.max(0, (ek/Math.max(1, totalE))*80)" width="8" :height="Math.max(0, (ek/Math.max(1, totalE))*80)" fill="#10b981" rx="1" />
             <rect x="22" :y="90 - Math.max(0, (ep/Math.max(1, totalE))*80)" width="8" :height="Math.max(0, (ep/Math.max(1, totalE))*80)" fill="#fbbf24" rx="1" />
             <text x="50" y="80" transform="rotate(-90, 50, 80)" fill="#64748b" font-size="7">能量条 (Ek|Ep)</text>
          </g>
        </svg>

        <!-- 这里的指示必须解决“调参没反应”的问题 -->
        <div class="realtime-status">
           {{ isRunning ? '状态: 释放滑行中...' : '状态: 初始高度设定' }} | v = {{ velocity.toFixed(1) }} m/s
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row">
              <div class="control-group">
                 <label class="s-label">初始投放高度 (h₀)</label>
                 <el-slider v-model="h0" :min="0" :max="100" :disabled="isRunning" size="small"></el-slider>
              </div>
              <div class="control-group">
                 <label class="s-label">摩擦损耗系数 (Optional)</label>
                 <el-slider v-model="friction" :min="0" :max="0.05" :step="0.001" size="small"></el-slider>
              </div>
              <div class="btn-group">
                 <el-button type="success" size="small" icon="el-icon-video-play" @click="start">释放小球</el-button>
                 <el-button size="small" icon="el-icon-refresh" @click="reset">复位</el-button>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-aim"></i>
           <p><strong>实战验证：</strong>注意观察！在摩擦系数为 0 时，随着高度 $h$ 降低，动能 $E_k$ 严格线性补足势能 $E_p$ 的亏损。不论轨道多复杂，<strong>总能量 E 保持恒定</strong>。如果滑块调大“阻力”，总能量将随着位移由于内能转化而单调递减。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'EnergyConservationViz',
  data() {
    return {
      h0: 80,
      friction: 0.002,
      progress: 0,
      isRunning: false,
      ballPos: { x: 50, y: 74 },
      velocity: 0,
      timer: null,
      lastTimestamp: 0
    }
  },
  computed: {
    displayTitle() {
      return this.knowledgePoint || '机械能守恒定律：高度与速度的转化';
    },
    ep() { 
       // Gravity potential energy depends on relative height (Y-axis)
       return (210 - this.ballPos.y) * 0.5; 
    },
    ek() { return this.totalE - this.ep - this.frictionLoss; },
    frictionLoss() { return this.progress * this.friction * 1000; },
    totalE() {
      // Total Energy = Initial Potential Energy + a small kick to ensure it starts moving if h0 > 0
      const startY = 210 - this.h0 * 1.7;
      return (210 - startY) * 0.5 + (this.h0 > 0 ? 0.2 : 0);
    },
    isDumping() { return this.friction > 0; },
    trackPath() {
       const startY = 210 - this.h0 * 1.7;
       return `M 50,${startY} C 150,${startY} 180,210 250,210 C 320,210 380,100 450,100`;
    }
  },
  watch: {
    h0() {
       if (!this.isRunning) {
         this.progress = 0;
         this.updatePos(0);
       }
    }
  },
  beforeDestroy() {
     if (this.animFrame) cancelAnimationFrame(this.animFrame);
  },
  methods: {
    start() {
       if (this.isRunning) return;
       this.isRunning = true;
       this.animate();
    },
    reset() {
       this.isRunning = false;
       this.progress = 0;
       this.velocity = 0;
       cancelAnimationFrame(this.animFrame);
       this.updatePos(0);
    },
    updatePos(p) {
       const t = p / 100;
       const startY = 210 - this.h0 * 1.7;
       if (t < 0.5) {
          const t1 = t * 2;
          this.ballPos.x = 50 + t1 * 200;
          this.ballPos.y = startY + Math.pow(t1, 1.5) * (210 - startY);
       } else {
          const t2 = (t - 0.5) * 2;
          this.ballPos.x = 250 + t2 * 200; // Fixed: 250 + 200 = 450
          this.ballPos.y = 210 - Math.pow(t2, 1.5) * 110;
       }
    },
    animate() {
       if (!this.isRunning) return;
       
       // Kinetic energy driven velocity: v = sqrt(2 * Ek / m)
       // Increase scaling factor from 0.15 to 0.4 for better visibility
       const v_sim = Math.sqrt(Math.max(0, this.ek)) * 0.45;
       this.velocity = v_sim * 10;
       
       // Ensure a minimum movement speed if we have potential to move
       const min_v = this.h0 > 5 ? 0.05 : 0;
       const final_v = Math.max(v_sim, min_v);
       
       if (this.progress < 100) {
          this.progress += final_v;
          this.updatePos(this.progress);
       } else {
          this.isRunning = false;
          this.velocity = 0;
       }
       
       this.animFrame = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.updatePos(0); }
}
</script>

<style scoped lang="scss">
.energy-con-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #fefce8; color: #854d0e; border: 1px solid #fef08a; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #10b981; } &.orange { border-left: 3px solid #fbbf24; } } }
.realtime-status { position: absolute; bottom: 12px; width: 100%; text-align: center; color: #475569; font-size: 10px; font-weight: bold; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; .flex-row { display: flex; gap: 15px; align-items: flex-end; .control-group { flex: 1; .s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; } } .btn-group { display: flex; gap: 8px; } } }
.discovery-card-compact { background: #f0fdf4; padding: 10px 14px; border-radius: 10px; border: 1px solid #dcfce7; display: flex; align-items: center; gap: 10px; i { color: #10b981; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 11px; color: #166534; line-height: 1.5; strong { color: #14532d; } } }
</style>
